import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Container from "@mui/material/Container";
import Avatar from "@mui/material/Avatar";
import { brandBlack, grey3 } from "theme/colors";
import { Grid, Menu, MenuItem, Tooltip } from "@mui/material";
import Logo from "./logo.png";
import { Aligned } from "components/Layout";
import { useCallback, useEffect, useMemo, useState } from "react";
import { HelpDialog } from "./HelpDialog";
import { useLocation, useNavigate } from "@reach/router";
import useKeyPress from "lib/keypress";
import { getPollInterval } from "lib/poller";
import { useDispatch } from "react-redux";
import { removeSubjectUpdateId } from "actions/subjects";
import { openSnackBarWithError, openSnackBarWithSuccess } from "actions/snackBar";
import store from "store";
import { IState, ModalDialog, SiteUpdateStatus } from "store/types";
import { isEmpty } from "lodash";
import { get } from "lib/fetch";
import {
  COOKIE_JWT_ACCESS_TOKEN,
  COOKIE_JWT_ID_TOKEN,
  getSessionTimeLeftMinutes,
  getUser,
  removeCookie,
} from "lib/Cookies";
import { useSelector } from "react-redux";
import { getConfig } from "actions/config";
import { isAdminUser } from "lib/utils";
import SettingsIcon from "@mui/icons-material/Settings";
import { CancelConfirmationDialog } from "containers/alignmentTool/CancelConfirmationDialog";
import QueryBuilderOutlinedIcon from "@mui/icons-material/QueryBuilderOutlined";
import { RefreshSessionDialog } from "containers/RefreshSessionDialog";

const avatarStyle = {
  color: brandBlack.toString(),
  bgcolor: grey3.toString(),
  fontSize: "13px",
};

function ResponsiveAppBar() {
  const navigate = useNavigate();

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getConfig());
  }, [dispatch]);

  // What we need from Redux
  const config = useSelector((state: IState) => state.config);

  const [openModal, setOpenModal] = useState(ModalDialog.NONE);
  const handleCloseModal = useCallback(() => setOpenModal(ModalDialog.NONE), []);
  const handleOpenModal = useCallback(() => setOpenModal(ModalDialog.CANCEL), []);
  const handleRefreshModal = useCallback(() => setOpenModal(ModalDialog.REFRESH), []);

  const path = useLocation().pathname;
  // only display help if in wizard
  const showKeyboardShortcutsMenuItem = path.includes("StepTwo");
  // only display abandon changes dialog before opening admin, if in wizard
  const showAbandonChangesDialog = path.includes("StepTwo");

  const user = getUser();
  const userInitials = user ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}` : "?";

  const [settingsEl, setSettingsEl] = useState<null | HTMLElement>(null);
  const settingMenuOpen = Boolean(settingsEl);
  const handleOpenSettings = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSettingsEl(event.currentTarget);
  };
  const handleCloseSettings = () => {
    setSettingsEl(null);
  };

  const onClickApplicationSettings = useCallback(() => {
    handleCloseSettings();
    if (showAbandonChangesDialog) {
      handleOpenModal();
    } else {
      navigate(`/admin`);
    }
  }, [handleOpenModal, navigate, showAbandonChangesDialog]);

  const [overrides, setOverrides] = useState<null | any>(null);
  const handleConfirm = useCallback(() => {
    handleCloseModal();
    navigate("/");
  }, [handleCloseModal, navigate]);

  const handleReturnToHome = useCallback(() => {
    if (!window.location.href.includes("wizardStepOne")) {
      if (window.location.href.includes("/admin/rule")) {
        setOverrides({
          title: "Abandon changes to your rules?",
          button: "Abandon my changes",
          message: "Any updates to the rules will be lost.",
        });
      } else if (window.location.href.includes("/admin")) {
        setOverrides({
          title: "Abandon changes?",
          button: "Abandon changes",
          message: "Any updates to the application settings will be lost.",
        });
      } else if (window.location.href.includes("/admin/rule")) {
        setOverrides({
          title: "Abandon changes to your rules?",
          button: "Abandon my changes",
          message: "Any updates to the rules will be lost.",
        });
      } else if (window.location.href.includes("/wizardStepTwo")) {
        setOverrides(null);
      }
      setOpenModal(ModalDialog.CANCEL);
    }
  }, []);

  const [sessionTime, setSessionTime] = useState(getSessionTimeLeftMinutes());

  const handleRefreshSession = useCallback(() => {
    removeCookie(COOKIE_JWT_ACCESS_TOKEN);
    removeCookie(COOKIE_JWT_ID_TOKEN);
    window.location.reload();
  }, []);

  const [openHelp, setOpenHelp] = useState(false);
  const handleOpenHelp = () => setOpenHelp(true);
  const handleCloseHelp = () => setOpenHelp(false);

  const showApplicationSettingsMenuItem = useCallback(() => {
    if (config && config.adminIds) {
      return !path.includes("admin") && isAdminUser(config.adminIds);
    }
    return false;
  }, [config, path]);

  const onKeyPress = (_event: any) => {
    setOpenHelp(true);
  };
  useKeyPress(["?"], onKeyPress);

  useMemo(() => {
    const interval = getPollInterval();
    if (interval !== "-1") {
      setInterval(async () => {
        const timeLeft = getSessionTimeLeftMinutes();
        setSessionTime(timeLeft);
        if (timeLeft > 0 && timeLeft < 5) {
          dispatch(openSnackBarWithError("Your session is about to expire.\nAny unsaved changes will be lost."));
        }
        const { updateIds } = store.getState();
        if (!isEmpty(updateIds)) {
          try {
            console.log(`Polling site update status for '${updateIds[0]}'...`);
            const response: any = await get(`/v1/siteupdate?id=${updateIds[0]}`);
            if (response) {
              const { status, _errors, name, updateId } = response;
              const completed = status === SiteUpdateStatus.SUCCESS || status === SiteUpdateStatus.ERROR;
              if (status === SiteUpdateStatus.SUCCESS) {
                dispatch(openSnackBarWithSuccess(`Alignment of '${name}' has completed successfully`));
                console.log(`Polling site update status for '${updateId}'... ${status}`);
              } else if (status === SiteUpdateStatus.ERROR) {
                dispatch(openSnackBarWithError(`Alignment of '${name}' has failed - check logs for details`));
                console.log(`Polling site update status for '${updateId}'... ${status}`);
                //  if (errors) {
                //  console.error("error:= ", JSON.stringify(errors));
                // }
              } else if (status === SiteUpdateStatus.IN_PROGRESS) {
                // do nothing, we need to retry at the next interval
                console.log(`Polling site update status for '${updateId}'... ${status}`);
              }
              if (completed) {
                // Remove from the updateIds list
                dispatch(removeSubjectUpdateId(updateId));
              }
            }
          } catch (error: any) {
            // Assume something bad - remove from the updateIds list
            dispatch(removeSubjectUpdateId(updateIds[0]));
            console.log(`Polling site update status for '${updateIds[0]}'... exception raised`);
            //console.error("Exception detail", JSON.stringify(error));
          }
        }
      }, interval);
    }
  }, [dispatch]);

  return (
    <>
      <AppBar position="static" color="secondary">
        <Container maxWidth={false}>
          <Toolbar disableGutters>
            <Box sx={{ flexGrow: 1 }}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={8}>
                  <Aligned>
                    <img src={Logo} style={{ alignSelf: "center" }} width="107" height="25" alt="Logo" />
                    <legend>
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                      <a onClick={handleReturnToHome} style={{ cursor: "pointer" }}>
                        LMSMorph
                      </a>
                    </legend>
                  </Aligned>
                </Grid>
                <Grid item xs={4} style={{ display: "flex", justifyContent: "right", alignItems: "right" }}>
                  <Aligned>
                    <QueryBuilderOutlinedIcon onClick={handleRefreshModal} style={{ cursor: "pointer" }} />
                    <legend style={{ fontSize: 12, lineHeight: "1.1" }}>
                      Session expires
                      <br />
                      in {sessionTime} mins
                    </legend>
                    &nbsp;&nbsp;
                    <IconButton onClick={handleOpenSettings} sx={{ p: 0 }}>
                      <Avatar sx={avatarStyle} alt="Settings">
                        <SettingsIcon />
                      </Avatar>
                    </IconButton>
                    &nbsp;&nbsp;
                    <Tooltip title={user?.email} placement="bottom">
                      <Avatar sx={avatarStyle} alt="LTU">
                        {userInitials}
                      </Avatar>
                    </Tooltip>
                  </Aligned>
                </Grid>
              </Grid>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      <HelpDialog open={openHelp} onClose={handleCloseHelp} />
      <Menu
        id="user-menu"
        anchorEl={settingsEl}
        open={settingMenuOpen && (showKeyboardShortcutsMenuItem || showApplicationSettingsMenuItem())}
        onClose={handleCloseSettings}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        {showKeyboardShortcutsMenuItem && <MenuItem onClick={handleOpenHelp}>Keyboard shortcuts</MenuItem>}
        {showApplicationSettingsMenuItem() && (
          <MenuItem onClick={onClickApplicationSettings}>Application settings</MenuItem>
        )}
      </Menu>
      <CancelConfirmationDialog
        open={openModal === ModalDialog.CANCEL}
        onConfirm={handleConfirm}
        onClose={handleCloseModal}
        overrideTitle={overrides ? overrides.title : undefined}
        overrideMessage={overrides ? overrides.message : undefined}
        overrideButton={overrides ? overrides.button : undefined}
      />
      <RefreshSessionDialog
        open={openModal === ModalDialog.REFRESH}
        onConfirm={handleRefreshSession}
        onClose={handleCloseModal}
      />
    </>
  );
}
export default ResponsiveAppBar;
